import { useEffect, useState } from 'react';
import { AlertCircle, BookText, BrainCircuit, ChevronDown, Database, InfoIcon, MessageSquare, Settings, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { APIKeySettings } from '../../components/settings/ai/APIKeySettings';
import { BotObjectives } from '../../components/settings/ai/BotObjectives';
import { KnowledgeBase } from '../../components/settings/ai/KnowledgeBase';
import { PromptSettings } from '../../components/settings/ai/PromptSettings';
import { TemperatureSettings } from '../../components/settings/ai/TemperatureSettings';
import { FineTuningSettings } from '../../components/settings/ai/FineTuningSettings';
import { useAuth } from '../../contexts/AuthContext';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useAISettings } from '../../hooks/useAISettings';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useSidebar } from '../../hooks/useSidebar';

// Character limit for AI settings
const CHARACTER_LIMIT = 100000;

// Default placeholder texts
const DEFAULT_GENERAL_PROMPT = "Describe the role and responsibilities of your assistant...";
const DEFAULT_ATTITUDE_PROMPT = "Describe the desired tone and behavior...";
const DEFAULT_KNOWLEDGE_TEXT = "Enter your text here...";
const DEFAULT_OBJECTIVE_TEXT = "Add a new goal...";

// Context for character count tracking
export const AISettingsContext = React.createContext<{
  characterCount: number;
  isOverLimit: boolean;
}>({
  characterCount: 0,
  isOverLimit: false
});

// Section component with collapsible functionality
interface AccordionSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({ 
  title, 
  icon, 
  defaultOpen = false,
  children,
  badge
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
      >
        <div className="flex items-center gap-3">
          <div className="text-indigo-400">
            {icon}
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {badge}
        </div>
        <ChevronDown 
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} 
        />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function AISettings() {
  const { user } = useAuth();
  const { chatbotPublicId } = useChatbot();
  const { initializeSettings, initialized, loading, prompts, knowledgeBase, objectives } = useAISettings();
  const [characterCount, setCharacterCount] = useState(0);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const { isOpen, setIsOpen } = useSidebar();

  // Calculate the total character count whenever the settings change
  useEffect(() => {
    if (!initialized) return;

    // Count prompts text (use placeholder text if empty)
    const generalPromptText = prompts.generalPrompt?.trim() || DEFAULT_GENERAL_PROMPT;
    const attitudePromptText = prompts.attitudePrompt?.trim() || DEFAULT_ATTITUDE_PROMPT;
    const promptsCharacters = generalPromptText.length + attitudePromptText.length;
    
    // Count knowledge base text
    const knowledgeBaseCharacters = knowledgeBase
      .map(item => (item.content?.trim() || DEFAULT_KNOWLEDGE_TEXT).length)
      .reduce((sum, length) => sum + length, 0);
    
    // Count objectives text
    const objectivesCharacters = objectives.length > 0 
      ? objectives.join('').length 
      : DEFAULT_OBJECTIVE_TEXT.length;
    
    const totalCharacters = promptsCharacters + knowledgeBaseCharacters + objectivesCharacters;
    
    setCharacterCount(totalCharacters);
    setIsOverLimit(totalCharacters > CHARACTER_LIMIT);
    
    // Auto-show warning when over limit
    if (totalCharacters > CHARACTER_LIMIT) {
      setShowLimitWarning(true);
    }
  }, [initialized, prompts, knowledgeBase, objectives]);

  useEffect(() => {
    if (!initialized && user && chatbotPublicId) {
      initializeSettings(chatbotPublicId);
    }
  }, [initialized, initializeSettings, user, chatbotPublicId]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen, setIsOpen]);

  if (!user) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-400">You need to be logged in to access this page.</p>
      </div>
    );
  }

  if (loading && !initialized) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }
  
  // Format the character percentage for display
  const characterPercentage = Math.min(100, Math.round((characterCount / CHARACTER_LIMIT) * 100));
  
  return (
    <AISettingsContext.Provider value={{ characterCount, isOverLimit }}>
      <div className="space-y-6 pb-8">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">AI settings</h2>
            <p className="text-gray-400">Set up the behavior and capabilities of your AI assistant.</p>
          </div>
          
          {/* Character usage indicator (pill) */}
          <button 
            onClick={() => setShowLimitWarning(!showLimitWarning)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${
              isOverLimit 
                ? 'bg-red-500/10 text-red-400 border-red-500/30' 
                : 'bg-green-500/10 text-green-400 border-green-500/30'
            }`}
          >
            <span className="text-sm font-medium">
              {characterPercentage}% Used
            </span>
            <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${isOverLimit ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${characterPercentage}%` }}
              />
            </div>
          </button>
        </div>

        {/* Error message when over limit */}
        <AnimatePresence>
          {showLimitWarning && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className={`p-4 rounded-lg flex items-start gap-3 ${
                isOverLimit ? 'bg-red-500/10 text-red-400' : 'bg-indigo-500/10 text-indigo-400'
              }`}>
                <div className="shrink-0 mt-0.5">
                  {isOverLimit ? <AlertCircle className="w-5 h-5" /> : <InfoIcon className="w-5 h-5" />}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <p className="font-medium">
                      {isOverLimit ? 'Character limit exceeded' : 'Character usage information'}
                    </p>
                    <button 
                      onClick={() => setShowLimitWarning(false)}
                      className="text-gray-400 hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm mt-1">
                    {isOverLimit 
                      ? `Your combined prompts, knowledge base, and objectives exceed the limit of ${CHARACTER_LIMIT.toLocaleString()} characters. Please reduce the content to save your changes.` 
                      : `You're using ${characterCount.toLocaleString()} out of ${CHARACTER_LIMIT.toLocaleString()} characters (${characterPercentage}%). This includes all prompts, knowledge base items, and objectives.`
                    }
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main content sections */}
        <div className="space-y-4">
          <AccordionSection 
            title="API Configuration" 
            icon={<SlidersHorizontal className="w-5 h-5" />}
            defaultOpen={true}
          >
            <div className="space-y-6">
              <APIKeySettings />
              <TemperatureSettings />
            </div>
          </AccordionSection>
          
          <AccordionSection 
            title="Prompt Engineering" 
            icon={<MessageSquare className="w-5 h-5" />}
            defaultOpen={true}
          >
            <PromptSettings />
          </AccordionSection>
          
          <AccordionSection 
            title="Knowledge Base" 
            icon={<Database className="w-5 h-5" />}
            badge={
              knowledgeBase.length > 0 ? (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-indigo-500/20 text-indigo-300">
                  {knowledgeBase.length} {knowledgeBase.length === 1 ? 'item' : 'items'}
                </span>
              ) : null
            }
          >
            <KnowledgeBase />
          </AccordionSection>
          
          <AccordionSection 
            title="Bot Objectives" 
            icon={<BookText className="w-5 h-5" />}
            badge={
              objectives.length > 0 ? (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-indigo-500/20 text-indigo-300">
                  {objectives.length} {objectives.length === 1 ? 'objective' : 'objectives'}
                </span>
              ) : null
            }
          >
            <BotObjectives />
          </AccordionSection>
          
          <AccordionSection 
            title="Fine-Tuning" 
            icon={<Settings className="w-5 h-5" />}
          >
            <FineTuningSettings />
          </AccordionSection>
          
          <AccordionSection 
            title="AI Features" 
            icon={<Sparkles className="w-5 h-5" />}
          >
            <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4 text-indigo-300">
              <div className="flex items-center gap-2 mb-2">
                <BrainCircuit className="w-5 h-5" />
                <h4 className="font-medium">Upcoming AI Features</h4>
              </div>
              <p className="text-sm">
                We're working on exciting new AI features, including custom model selection 
                and function calling capabilities. Try out our new fine-tuning options!
              </p>
            </div>
          </AccordionSection>
        </div>
      </div>
    </AISettingsContext.Provider>
  );
}